import { message } from 'antd';
import * as React from 'react';

type ApolloError = { extensions?: Record<string, unknown>; message: string };

export function createMessage(
  type: 'success' | 'error',
  content: React.ReactNode | string,
  duration = 3
) {
  switch (type) {
    case 'success':
      message.success(content, duration);
      break;
    case 'error':
      message.error(content, duration);
      break;
    default:
      // type is never, but we're in js land
      console.warn(`Invalid type (${type}) for createMessage`);
  }
}

export function extractErrorMessage(response: {
  errors?: readonly ApolloError[];
}) {
  const errors = response.errors;

  if (errors) {
    const errorCodes = errors.map((err) => err.extensions?.code);

    return [
      true,
      errorCodes.includes('UNAUTHORIZED') ? errors[0].message : null,
    ] as const;
  }

  return [false, null] as const;
}

export function mutationResponseMessage<T>({
  gqlResponse,
  onSuccess,
  errorMessage,
  afterError,
}: {
  gqlResponse: { data?: T; errors?: ApolloError[] };
  onSuccess: string | ((data: T) => void);
  errorMessage: string;
  afterError?: () => void;
}) {
  const defaultErrorMessage = 'Unable to perform this action';

  // TS helps on this one, but we're still in js land
  if (!gqlResponse) {
    throw new Error('gqlResponse required for responseMessage');
  }

  const [hadErrors, message] = extractErrorMessage(gqlResponse);

  if (hadErrors) {
    createMessage('error', message || errorMessage || defaultErrorMessage);
    afterError?.();
  } else {
    if (typeof onSuccess === 'string') {
      createMessage('success', onSuccess);
    } else {
      // in **theory** gql can send both data and error nulls.. but
      // that's not supposed to be a real case.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onSuccess(gqlResponse.data!);
    }
  }
}
